import React, { useEffect, useMemo, useState } from "react";
import { Info } from "lucide-react";
import SearchableSelect from "./SearchableSelect";
import { reportService } from "../api/reportService";
import { getDateRangeForPeriod } from "../utils/salesUtils";

export default function PendingTransactionsTable({ appliedRange }) {
  const [allTransactions, setAllTransactions] = useState([]);
  const [summary, setSummary] = useState({
    transaction_qty: 0,
    total_amount: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    const loadPendingTransactions = async () => {
      try {
        setLoading(true);
        setError("");

        let period;
        if (appliedRange?.startMonth && appliedRange?.endMonth) {
          const [startYear, startMonth] = appliedRange.startMonth.split("-").map(Number);
          const [endYear, endMonth] = appliedRange.endMonth.split("-").map(Number);
          const pad = (num) => String(num).padStart(2, "0");
          const lastDay = new Date(endYear, endMonth, 0).getDate();
          period = {
            start_date: `${startYear}-${pad(startMonth)}-01`,
            end_date: `${endYear}-${pad(endMonth)}-${pad(lastDay)}`,
          };
        } else {
          period = getDateRangeForPeriod("month");
        }

        const response = await reportService.getDashboard({
          ...period,
          pending_limit: 100,
        });

        const data = response?.data || {};
        setAllTransactions(data?.pending_list || []);
        setSummary({
          transaction_qty: Number(data?.pending_summary?.transaction_qty || 0),
          total_amount: Number(data?.pending_summary?.total_amount || 0),
        });
      } catch (err) {
        console.error("Failed to load pending transactions:", err);
        setError(err?.message || "Gagal memuat transaksi pending");
        setAllTransactions([]);
        setSummary({ transaction_qty: 0, total_amount: 0 });
      } finally {
        setLoading(false);
      }
    };

    loadPendingTransactions();
  }, [appliedRange]);

  const formatCurrency = (value) => {
    const num = Number(value || 0);
    if (!Number.isFinite(num)) return "Rp 0";
    return `Rp ${num.toLocaleString("id-ID")}`;
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const totalEntries = allTransactions.length;
  const maxPages = Math.max(1, Math.ceil(totalEntries / itemsPerPage));
  const safePage = Math.min(currentPage, maxPages);
  const startIndex = (safePage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTransactions = useMemo(() => {
    return allTransactions.slice(startIndex, endIndex);
  }, [allTransactions, startIndex, endIndex]);

  const startEntry = totalEntries === 0 ? 0 : startIndex + 1;
  const endEntry = Math.min(endIndex, totalEntries);

  useEffect(() => {
    if (currentPage > maxPages) {
      setCurrentPage(maxPages);
    }
  }, [currentPage, maxPages]);

  return (
    <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm overflow-hidden">
      <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 ring-1 ring-amber-100 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <h3 className="font-bold text-slate-900">Transaksi Pending</h3>
        </div>
        <div className="flex gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-rose-50 text-rose-700 ring-1 ring-rose-100 rounded-lg text-xs font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
            {summary.transaction_qty.toLocaleString("id-ID")} Transaksi
          </span>
          <span className="inline-flex items-center px-3 py-1 bg-rose-50 text-rose-700 ring-1 ring-rose-100 rounded-lg text-xs font-semibold">
            {formatCurrency(summary.total_amount)}
          </span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
              <th className="px-6 py-3">Aksi</th>
              <th className="px-6 py-3">Nomor Transaksi</th>
              <th className="px-6 py-3">Waktu Transaksi</th>
              <th className="px-6 py-3">Nama Pelanggan</th>
              <th className="px-6 py-3">Jenis</th>
              <th className="px-6 py-3">Pembayaran</th>
              <th className="px-6 py-3">Total Belanja</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td
                  colSpan={9}
                  className="px-6 py-6 text-center text-sm text-slate-500">
                  Memuat transaksi pending...
                </td>
              </tr>
            )}

            {!loading && error && (
              <tr>
                <td
                  colSpan={9}
                  className="px-6 py-6 text-center text-sm text-rose-600">
                  {error}
                </td>
              </tr>
            )}

            {!loading && !error && paginatedTransactions.length === 0 && (
              <tr>
                <td
                  colSpan={9}
                  className="px-6 py-6 text-center text-sm text-slate-500">
                  Tidak ada transaksi pending
                </td>
              </tr>
            )}

            {!loading &&
              !error &&
              paginatedTransactions.map((tx, idx) => (
                <tr
                  key={tx.order_id || tx.transaction_no || idx}
                  className="hover:bg-slate-50/70 transition-colors">
                  <td className="px-6 py-3.5">
                    <button
                      className="w-8 h-8 bg-teal-600 text-white rounded-lg flex items-center justify-center hover:bg-teal-700 transition-colors"
                      title="Informasi transaksi">
                      <Info className="w-3.5 h-3.5" />
                    </button>
                  </td>
                  <td className="px-6 py-3.5 text-sm font-medium text-slate-900">
                    {tx.transaction_no || "-"}
                  </td>
                  <td className="px-6 py-3.5 text-sm text-slate-500">
                    {formatDate(tx.order_date)}
                  </td>
                  <td className="px-6 py-3.5 text-sm text-slate-700">
                    {tx.customer || "Pelanggan Umum"}
                  </td>
                  <td className="px-6 py-3.5 text-sm text-slate-500">
                    {tx.transaction_type || "-"}
                  </td>
                  <td className="px-6 py-3.5 text-sm text-slate-500">
                    {tx.payment_method || "-"}
                  </td>
                  <td className="px-6 py-3.5 text-sm font-semibold text-slate-900">
                    {formatCurrency(tx.grand_total)}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div className="px-5 py-3.5 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <span>
            Showing {startEntry} to {endEntry} of {totalEntries} entries
          </span>
          <SearchableSelect
            options={[
              { value: 10, label: "10" },
              { value: 25, label: "25" },
              { value: 50, label: "50" },
              { value: 100, label: "100" },
            ]}
            value={itemsPerPage}
            onChange={(value) => {
              setItemsPerPage(Number(value) || 10);
              setCurrentPage(1);
            }}
            placeholder="Items per page"
            displayKey="label"
            valueKey="value"
            className="w-20"
            searchable={false}
            size="sm"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={safePage === 1}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700 disabled:opacity-40 disabled:hover:bg-slate-100 transition-colors">
            ‹‹
          </button>
          <button
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={safePage === 1}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700 disabled:opacity-40 disabled:hover:bg-slate-100 transition-colors">
            ‹
          </button>
          <button className="min-w-[32px] h-8 px-2.5 flex items-center justify-center rounded-lg bg-teal-600 text-white text-sm font-semibold shadow-sm shadow-teal-600/20">
            {safePage}
          </button>
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(maxPages, prev + 1))
            }
            disabled={safePage >= maxPages}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700 disabled:opacity-40 disabled:hover:bg-slate-100 transition-colors">
            ›
          </button>
          <button
            onClick={() => setCurrentPage(maxPages)}
            disabled={safePage >= maxPages}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700 disabled:opacity-40 disabled:hover:bg-slate-100 transition-colors">
            ››
          </button>
        </div>
      </div>
    </div>
  );
}
